import i18n from '@/i18n';
import type { Router } from 'vue-router';
import { getCurrentInstance } from 'vue';
import {
	toastError,
	toastSuccess,
	toastInfo,
	toastWarning,
} from '@arritech/ui/Toast';
import { endExpiredSession } from '@/utils/sessionExpiry';

const t = i18n.global.t;

// A failure with no specific message to show gets longer than the 3s default
// to be read.
const GENERIC_ERROR_DURATION_MS = 6000;

// A toast with no duration has no timer, so it stays until it is closed.
export const UNTIL_CLOSED = 0;

type MessageOptions = { title?: string; duration?: number };

export function createErrorHandler(deps: { router: Router }) {
	const { router } = deps;

	const errorMsg = (msg: string, opts: MessageOptions = {}) =>
		toastError({ message: msg, ...opts });
	const successMsg = (msg: string, opts: MessageOptions = {}) =>
		toastSuccess({ message: msg, ...opts });
	const infoMsg = (msg: string, opts: MessageOptions = {}) =>
		toastInfo({ message: msg, ...opts });
	const warningMsg = (msg: string, opts: MessageOptions = {}) =>
		toastWarning({ message: msg, ...opts });

	const genericErrorMsg = (title: string, message: string) =>
		errorMsg(message, { title, duration: GENERIC_ERROR_DURATION_MS });

	// Returns the id of the toast it shows, so the caller can close it again.
	const handleHttpStatus = (
		err: any,
		defaultTitle: string,
		defaultMessage: string
	): string | undefined => {
		if (!err.response) {
			return genericErrorMsg(
				t('errors.connection.title'),
				t('errors.connection.tryAgainLater')
			);
		}

		const status = err.response.status;

		if (status === 401 || status === 403 || status === 404) {
			if (err.response.data) {
				if (err.response.data.errors) {
					const isLoginPage =
						window.location.href.endsWith('/login') ||
						window.location.href.endsWith('/');

					// A wrong password stays on screen until the user closes it.
					if (isLoginPage && status === 401) {
						return errorMsg(t('auth.login.errorBody'), {
							title: t('errors.title'),
							duration: UNTIL_CLOSED,
						});
					}
					return errorMsg(err.response.data.errors[0].message);
				}
				if (err.response.data.error_message) {
					return errorMsg(err.response.data.error_message);
				}
				return genericErrorMsg(defaultTitle, defaultMessage);
			}
			localStorage.setItem('authenticated_in_prev_check', 'false');
			void endExpiredSession(router);
			return undefined;
		}

		if (status && !err.response.data?.data && err.response.data?.errors) {
			return errorMsg(err.response.data.errors[0].message);
		}

		return genericErrorMsg(defaultTitle, defaultMessage);
	};

	return {
		handleHttpStatus,
		errorMsg,
		successMsg,
		infoMsg,
		warningMsg,
	};
}

/**
 * Composable for Composition API components (Vue 2 + compat)
 */
export function useErrorHandler() {
	const vm = getCurrentInstance()?.proxy as any;
	if (!vm) {
		throw new Error('useErrorHandler() must be called inside setup().');
	}

	return createErrorHandler({
		router: vm.$router,
	});
}

/**
 * Options API mixin (legacy components)
 */
export default {
	computed: {
		// create once, reuse everywhere
		_errorHandler(): ReturnType<typeof createErrorHandler> {
			return createErrorHandler({
				router: (this as any).$router,
			});
		},
	},
	methods: {
		handleHttpStatus(err: any, defaultTitle: string, defaultMessage: string) {
			return (this as any)._errorHandler.handleHttpStatus(
				err,
				defaultTitle,
				defaultMessage
			);
		},
		errorMsg(msg: string, opts?: MessageOptions) {
			return (this as any)._errorHandler.errorMsg(msg, opts);
		},
		successMsg(msg: string, opts?: MessageOptions) {
			return (this as any)._errorHandler.successMsg(msg, opts);
		},
		infoMsg(msg: string, opts?: MessageOptions) {
			return (this as any)._errorHandler.infoMsg(msg, opts);
		},
		warningMsg(msg: string, opts?: MessageOptions) {
			return (this as any)._errorHandler.warningMsg(msg, opts);
		},
	},
};
